import React, { type ReactNode } from 'react';
import { styled } from '@compiled/react';
import { useYbrFeature } from '@jira/yellow-brick-road-utils/src/controllers/useYbrFeature.tsx';
import { ModalHeader, ModalTitle } from '@atlaskit/modal-dialog';
import { Box, xcss } from '@atlaskit/primitives';
import { expVal } from '@atlassian/jira-feature-experiments';
import { useIntl } from '@atlassian/jira-intl';
import { useFormContext } from '@atlassian/jira-issue-create-form-context/src/index.tsx';
import type { WatchersConfig } from '@atlassian/jira-issue-create-watchers/src/services/types.tsx';
import { ModalHeader as MinimizableModalHeader } from '@atlassian/jira-minimizable-modal/src/ui/modal-container/modal-header/index.tsx';
import { useTriggerIssueCreateModal } from '@atlassian/jira-packages-controllers-use-trigger-issue-create-modal/src/main.tsx';
import { isMobileAndInMvpOrExistingUsersExperiment } from '@atlassian/jira-mobile-web/src/index.tsx';
import { useIsGICPositioningEnabled } from '../../../../../controllers/gic-positioning-enabled/main.tsx';
import Watchers from '../../../create-form/actions-container/watchers/index.tsx'; // This would be cleaned up together with gic positioning
import { HeaderDropdown as NewHeaderDropdown } from '../../../create-form/actions-container/dropdown-menu/index.tsx';
import { useDefaultWatchers } from '../../../create-form/actions-container/use-default-watchers/index.tsx';
import HeaderDropdown from './dropdown-menu/index.tsx';
import messages from './messages.tsx';
import { SpotlightContainer } from './spotlight-container/index.tsx';

export type Props = {
	shouldShowChangerSpotlight?: boolean;
	dismissModalEntryPointWrapper?: React.FC<{
		children: React.ReactNode;
	}>;
	watchersConfigurations?: WatchersConfig;
};

export const NewHeader = ({
	shouldShowChangerSpotlight,
	dismissModalEntryPointWrapper,
	watchersConfigurations,
}: Props) => {
	const { formatMessage } = useIntl();
	const [{ title, isSubTaskCreationOpen, isCreateLinkedIssue }] = useTriggerIssueCreateModal();
	const [{ dataFetchError, selectedProject, selectedIssueType }] = useFormContext();
	const issueType = selectedIssueType?.label;
	const { shouldShowYbrMessage } = useYbrFeature();

	let titleMsg =
		issueType && expVal('jira-itr-work-type-exp-1', 'isEnabled', false)
			? messages.titleIssueTerminologyRefreshM4
			: messages.title;

	if (isSubTaskCreationOpen) {
		titleMsg = messages.subtaskTitle;
	} else if (isCreateLinkedIssue) {
		titleMsg = expVal('issue-terminology-refresh-m2-replace', 'isEnabled', false)
			? messages.createLinkedIssueDialogTitleIssueTermRefresh
			: messages.createLinkedIssueDialogTitle;
	}

	const shouldShowChangerSpotlightBoolean = !!shouldShowChangerSpotlight;

	const ViewChangerContainer = shouldShowYbrMessage({
		shouldDisplayMessage: shouldShowChangerSpotlightBoolean,
	})
		? SpotlightContainer
		: undefined;

	const shouldDisableMeatBallActions = dataFetchError !== null || selectedProject === null;
	const meatBallMenu = !isMobileAndInMvpOrExistingUsersExperiment() ? (
		<NewHeaderDropdown
			isDisabled={shouldDisableMeatBallActions}
			watchersConfigurations={watchersConfigurations}
		/>
	) : null;

	useDefaultWatchers({ configs: watchersConfigurations });

	return (
		// eslint-disable-next-line jira/react/no-empty-divs
		<div>
			<MinimizableModalHeader
				ViewChangerContainer={ViewChangerContainer}
				dismissModalEntryPointWrapper={dismissModalEntryPointWrapper}
				meatBallMenu={meatBallMenu}
				project={selectedProject || undefined}
			>
				{title !== null && title !== '' ? title : formatMessage(titleMsg, { issueType })}
			</MinimizableModalHeader>
		</div>
	);
};

export type PropsOld = {
	shouldShowChangerSpotlight?: boolean;
	shouldDisableActions: boolean;
	watchersConfigurations?: WatchersConfig;
	dismissModalEntryPointWrapper?: React.FC<{
		children: React.ReactNode;
	}>;
};

const OldHeader = ({ shouldDisableActions, watchersConfigurations }: PropsOld) => {
	const [{ selectedProject, selectedIssueType }] = useFormContext();
	const { formatMessage } = useIntl();
	const [{ title, isSubTaskCreationOpen, isCreateLinkedIssue }] = useTriggerIssueCreateModal();
	const issueType = selectedIssueType?.label;

	let titleMsg =
		issueType && expVal('jira-itr-work-type-exp-1', 'isEnabled', false)
			? messages.titleIssueTerminologyRefreshM4
			: messages.title;

	const shouldDisableHeaderDropdown = (): boolean =>
		Boolean(shouldDisableActions || selectedProject === null);

	if (isSubTaskCreationOpen) {
		titleMsg = messages.subtaskTitle;
	} else if (isCreateLinkedIssue) {
		titleMsg = expVal('issue-terminology-refresh-m2-replace', 'isEnabled', false)
			? messages.createLinkedIssueDialogTitleIssueTermRefresh
			: messages.createLinkedIssueDialogTitle;
	}

	return (
		// eslint-disable-next-line jira/react/no-empty-divs
		<div>
			<ModalHeader>
				{/* eslint-disable-next-line jira/react/no-empty-divs */}
				<div>
					<ModalTitle>
						{title !== null && title !== '' ? title : formatMessage(titleMsg, { issueType })}
					</ModalTitle>
				</div>

				<ActionsContainer>
					<Watchers configs={watchersConfigurations} />
					<Box xcss={dropdownContainerStyles}>
						<HeaderDropdown isDisabled={shouldDisableHeaderDropdown()} />
					</Box>
				</ActionsContainer>
			</ModalHeader>
		</div>
	);
};

export const Header = (props: PropsOld) => {
	const isGICPositioningEnabled = useIsGICPositioningEnabled();

	if (isGICPositioningEnabled()) {
		return (
			<NewHeader
				shouldShowChangerSpotlight={props.shouldShowChangerSpotlight}
				dismissModalEntryPointWrapper={props.dismissModalEntryPointWrapper}
				watchersConfigurations={props.watchersConfigurations}
			/>
		);
	}
	return <OldHeader {...props} />;
};

export default Header;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ActionsContainer = styled.div<{
	children?: ReactNode;
}>({
	marginLeft: 'auto',
	display: 'flex',
});

const dropdownContainerStyles = xcss({
	marginLeft: 'space.100',
});
